/* eslint-disable react/no-multi-comp */
/* eslint-disable @typescript-eslint/consistent-type-definitions */
import {
    forwardRef,
    ComponentType,
    Component,
    RefObject,
    // ReactNode,
    // FunctionComponent,
    // PropsWithoutRef, PropsWithChildren,
} from 'react';
import EventEmitter from 'events';
import {isEqual, cloneDeep} from 'lodash-es';
import assert from 'assert';
// import {WithIntlProps} from 'react-intl';
import {StoreData} from '../interface';
import {KEYS_DEFAULT_VALUE} from './constant';

type Callback = (...args: any[]) => void;

type KeyCallback = (...args: any[]) => string[];

type Key = string | KeyCallback;

type KeyOrKeys = Key | Key[];

class SimpleStore {
    data: StoreData;

    listener: EventEmitter;

    constructor() {
        this.data = {...KEYS_DEFAULT_VALUE};
        this.listener = new EventEmitter();
        EventEmitter.defaultMaxListeners = 0;
    }

    subscribe(key: string, listener: Callback) {
        //    console.debug(`subscribe ${key}`);
        this.listener.on(key, listener);
    }

    unsubscribe(key: string, listener: Callback) {
        //    console.debug(`unsubscribe ${key}`);
        this.listener.off(key, listener);
    }

    setData = (key: string, data: any, sync: boolean = false) => {
        const old = this.data[key];
        if (isEqual(data, old)) {
            return;
        }
        this.data[key] = data;

        if (sync) {
            this.listener.emit(key, key, data);
        } else {
            setTimeout(() => {
                this.listener.emit(key, key, data);
            }, 0);
        }
    };

    getData = (key: string, clone: boolean = true) => {
        return clone ? cloneDeep(this.data[key]) : this.data[key];
    };

    hasKey(key: string) {
        return this.data[key] !== undefined;
    }
}

const store = new SimpleStore();

export default store;

export function connect(keyOrKeys: KeyOrKeys) {
    //
    function toArray(o: KeyOrKeys) {
        if (Array.isArray(o)) {
            return o;
        }
        return [o];
    }
    //
    const keys = toArray(keyOrKeys);
    //
    return function wrapWithConnect<
        T extends ComponentType,
        // eslint-disable-next-line @typescript-eslint/ban-types
        OriginalProps extends {}
    >(WrappedComponent: ComponentType<OriginalProps>) {
        //
        interface PrivateProps {
            forwardedRef: RefObject<T>;
        }
        type ConnectProps = OriginalProps & PrivateProps;
        //
        class Connect extends Component<ConnectProps> {
            nextProps: ConnectProps;

            constructor(props: ConnectProps) {
                super(props);
                this.nextProps = props;
                //
                const org: StoreData = {};
                keys.forEach(key => {
                    const k = key.toString();
                    org[k] = store.getData(k);
                });
                this.state = org;
            }

            componentDidMount() {
                //
                keys.forEach(key => {
                    if (typeof key === 'function') {
                        const subKeys = key(this.props);
                        toArray(subKeys).forEach(subKey => {
                            store.subscribe(subKey.toString(), this.handleDataChange);
                        });
                    } else {
                        store.subscribe(key, this.handleDataChange);
                    }
                });
            }

            shouldComponentUpdate(nextProps: T & ConnectProps) {
                this.nextProps = nextProps;
                return true;
            }

            componentWillUnmount() {
                keys.forEach(key => {
                    if (typeof key === 'function') {
                        const subKeys = key(this.props);
                        toArray(subKeys).forEach(subKey => {
                            assert(typeof subKey === 'string');
                            store.unsubscribe(subKey, this.handleDataChange);
                        });
                    } else {
                        store.unsubscribe(key, this.handleDataChange);
                    }
                });
            }

            handleDataChange = (key: string, data: any) => {
                const state: StoreData = {};
                // 因为有了初始值 如果要改变的值和初始值一致 不进行setState 避免多次渲染
                // @ts-ignore
                if (isEqual(this.state[key], data)) {
                    return;
                }
                state[key] = data;
                this.setState(state);
            };

            render() {
                const {forwardedRef, ...rest} = this.nextProps as PrivateProps;
                const refs: OriginalProps = {
                    ref: forwardedRef,
                } as any;
                return <WrappedComponent {...refs} {...rest} {...(this.state as OriginalProps)} />;
            }
        }

        const RefForwardingFactory = (props: ConnectProps, ref: RefObject<T>) => (
            <Connect {...props} forwardedRef={ref} />
        );

        return forwardRef<T, OriginalProps>(RefForwardingFactory as any);
    };
}

/**
 * 连接 store，用于获取 store 中的数据
 * 优化了类型，更少的使用可选的 props
 *
 * @param keyOrKeys 要连接的 store 的 key 或 keys
 * @returns 返回一个高阶组件，用于将传入的组件与 store 连接起来
 */
export function connectStore<StoreProps, OwnProps>(keyOrKeys: KeyOrKeys) {
    type MergedProps = StoreProps & OwnProps;

    function toArray(o: KeyOrKeys) {
        if (Array.isArray(o)) {
            return o;
        }
        return [o];
    }
    const keys = toArray(keyOrKeys);

    return function wrapWithConnect<T>(WrappedComponent: ComponentType<MergedProps>) {
        interface RefProps {
            forwardedRef: React.ForwardedRef<T>;
        }

        type ConnectProps = MergedProps & RefProps;

        class Connect extends Component<OwnProps> {
            nextProps: ConnectProps;

            constructor(props: ConnectProps) {
                super(props);
                this.nextProps = props;
                const org: any = {};
                keys.forEach(key => {
                    const k = key.toString();
                    org[k] = store.getData(k);
                });
                this.state = org;
            }

            componentDidMount() {
                keys.forEach(key => {
                    if (typeof key === 'function') {
                        const subKeys = key(this.props);
                        toArray(subKeys).forEach(subKey => {
                            store.subscribe(subKey.toString(), this.handleDataChange);
                        });
                    } else {
                        store.subscribe(key, this.handleDataChange);
                    }
                });
            }

            shouldComponentUpdate(nextProps: ConnectProps) {
                this.nextProps = nextProps;
                return true;
            }

            componentWillUnmount() {
                keys.forEach(key => {
                    if (typeof key === 'function') {
                        const subKeys = key(this.props);
                        toArray(subKeys).forEach(subKey => {
                            assert(typeof subKey === 'string');
                            store.unsubscribe(subKey, this.handleDataChange);
                        });
                    } else {
                        store.unsubscribe(key, this.handleDataChange);
                    }
                });
            }

            handleDataChange = (key: string, data: any) => {
                const state: StoreData = {};
                // 因为有了初始值 如果要改变的值和初始值一致 不进行setState 避免多次渲染
                // @ts-ignore
                if (isEqual(this.state[key], data)) {
                    return;
                }
                state[key] = data;
                this.setState(state);
            };

            render() {
                return <WrappedComponent {...this.nextProps} {...this.state} />;
            }
        }

        const RefForwardingFactory = (props: OwnProps, ref: React.ForwardedRef<T>) => (
            <Connect {...props} forwardedRef={ref} />
        );

        return forwardRef<T, OwnProps>(RefForwardingFactory);
    };
}
